import { useMutation } from '@tanstack/react-query';
import { Badge } from '@givebutter/primitives/badge';
import { useToast } from '@givebutter/primitives/toast';
import { faSparkles } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { updateAccount } from 'api/account';
import * as Form from 'components/form';
import * as SettingsCard from 'components/settings-card';
import { BETA_ACCESS_GUIDE_LINK } from 'constants/links';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { GivebutterAxiosError } from 'types/error';

const BetaSettings = () => {
    const toast = useToast();
    const { accountId, isBeta } = useCurrentAccount();
    const methods = Form.useGivebutterForm({
        defaultValues: {
            is_beta: isBeta,
        },
    });
    const { handleSubmit, watch, control } = methods;
    const { is_beta } = watch();
    const { mutate: submit } = useMutation({
        mutationFn: () => updateAccount(accountId, { is_beta }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.current(accountId) });
            toast.success('Updated account settings');
        },
        onError: (error: GivebutterAxiosError) => {
            toast.error(<>{error.response?.data.message ?? 'Unknown error occurred'}</>);
        },
    });

    return (
        <SettingsCard.Root>
            <SettingsCard.LeftSide>
                <SettingsCard.Heading>Early Access Beta</SettingsCard.Heading>
            </SettingsCard.LeftSide>
            <SettingsCard.RightSide>
                <Form.GivebutterFormProvider {...methods}>
                    <form onSubmit={handleSubmit(() => submit())}>
                        <Form.Container className={'flex flex-row justify-between gap-4'}>
                            <div className={'flex flex-col gap-2'}>
                                <Form.Label
                                    name="is_beta"
                                    className="flex flex-wrap items-center gap-2"
                                >
                                    <Badge className="relative px-4">
                                        <FontAwesomeIcon
                                            icon={faSparkles}
                                            className="text-brand-butter-primary absolute -left-0.5 -top-1"
                                        />
                                        Beta
                                    </Badge>
                                    Enable Early Access Beta
                                </Form.Label>
                                <div className={'flex flex-col gap-3'}>
                                    <p className={'text-sm text-gray-600'}>
                                        Turning this on will give you early access to new beta and
                                        experimental features. Note: this may include fundraising
                                        tools and donor-facing experiences.{' '}
                                        <a
                                            href={BETA_ACCESS_GUIDE_LINK}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            Learn more
                                        </a>
                                    </p>
                                </div>
                            </div>
                            <Form.Switch
                                name={'is_beta'}
                                control={control}
                                onCheckedChange={() => submit()}
                            />
                        </Form.Container>
                    </form>
                </Form.GivebutterFormProvider>
            </SettingsCard.RightSide>
        </SettingsCard.Root>
    );
};

export { BetaSettings };
